import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  TextField,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import type { JsonObject } from '@backstage/types';
import {
  configApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
  type ApiHolder,
} from '@backstage/core-plugin-api';

export interface GitLabUsernameConditionalValue {
  /** When true, access is requested for another person (not the signed-in user). */
  raising_for_other: boolean;
  other_username?: string;
  /** GitLab username from the signed-in identity (catalog User name). */
  self_username?: string;
}

const defaultValue: GitLabUsernameConditionalValue = {
  raising_for_other: false,
  other_username: '',
  self_username: '',
};

function isTruthyFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    return s === 'true' || s === 'yes' || s === '1' || s === 'on';
  }
  return false;
}

/** Extract username from Backstage user entity ref (user:default/name → name). */
export function usernameFromUserEntityRef(userEntityRef: string): string {
  const raw = userEntityRef.trim();
  if (!raw) return '';
  const withoutKind = raw.includes(':') ? raw.slice(raw.indexOf(':') + 1) : raw;
  const name = withoutKind.includes('/')
    ? withoutKind.slice(withoutKind.lastIndexOf('/') + 1)
    : withoutKind;
  return name.trim();
}

/** Scaffolder field validation for the username / other-user section. */
export async function validateGitLabUsernameConditional(
  value: GitLabUsernameConditionalValue | undefined,
  validation: FieldValidation,
  context: { apiHolder: ApiHolder; formData?: JsonObject },
): Promise<void> {
  const v = value ?? defaultValue;

  if (!isTruthyFlag(v.raising_for_other)) {
    let selfUsername = v.self_username?.trim() ?? '';
    // Form can validate before the field's useEffect writes self_username into
    // formData (UI may already show the name from local state). Resolve from
    // the signed-in identity as a fallback.
    if (!selfUsername) {
      try {
        const identityApi = context.apiHolder.get(identityApiRef);
        if (identityApi) {
          const identity = await identityApi.getBackstageIdentity();
          selfUsername = usernameFromUserEntityRef(
            identity?.userEntityRef ?? '',
          );
        }
      } catch {
        // fall through to error below
      }
    }
    if (!selfUsername) {
      validation.addError(
        'Could not load your GitLab username from the signed-in session.',
      );
    }
    return;
  }

  const otherUsername = v.other_username?.trim() ?? '';
  if (!otherUsername) {
    validation.addError("Enter the other user's GitLab username.");
    return;
  }

  const configApi = context.apiHolder.get(configApiRef);
  const fetchApi = context.apiHolder.get(fetchApiRef);
  if (!configApi || !fetchApi) {
    validation.addError('Unable to validate user (APIs unavailable).');
    return;
  }

  const backendBaseUrl = configApi.getString('backend.baseUrl');
  const params = new URLSearchParams({ username: otherUsername });
  const url = `${backendBaseUrl}/api/gitlab-access-request/resolve-user?${params}`;

  try {
    const res = await fetchApi.fetch(url);
    const data = (await res.json().catch(() => ({}))) as {
      valid?: boolean;
      message?: string;
      error?: string;
    };
    if (!res.ok || data.valid === false) {
      validation.addError(
        data.message ||
          data.error ||
          `Could not validate other user (HTTP ${res.status}).`,
      );
    }
  } catch (e) {
    validation.addError(
      e instanceof Error
        ? `User validation failed: ${e.message}`
        : 'User validation failed.',
    );
  }
}

export const GitLabUsernameConditionalField = ({
  onChange,
  rawErrors,
  formData,
}: FieldExtensionComponentProps<GitLabUsernameConditionalValue>) => {
  const identityApi = useApi(identityApiRef);
  const [loadingIdentity, setLoadingIdentity] = useState(true);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [selfUsername, setSelfUsername] = useState(
    formData?.self_username?.trim() || '',
  );

  const value: GitLabUsernameConditionalValue = useMemo(
    () => ({
      ...defaultValue,
      ...(formData ?? {}),
      self_username: formData?.self_username || selfUsername,
    }),
    [formData, selfUsername],
  );

  const setValue = useCallback(
    (next: GitLabUsernameConditionalValue) => {
      onChange(next);
    },
    [onChange],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const fromRef = usernameFromUserEntityRef(
          identity?.userEntityRef ?? '',
        );
        if (cancelled) return;
        setLoadingIdentity(false);
        if (!fromRef) {
          setIdentityError('Signed-in GitLab username was not found.');
          return;
        }
        setIdentityError(null);
        setSelfUsername(fromRef);
        const current = formData ?? defaultValue;
        onChange({
          ...defaultValue,
          ...current,
          self_username: fromRef,
        });
      } catch (e) {
        if (!cancelled) {
          setLoadingIdentity(false);
          setIdentityError(
            e instanceof Error
              ? e.message
              : 'Failed to load signed-in GitLab username',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityApi]);

  const handleRaisingForOther = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setValue({
        ...value,
        raising_for_other: checked,
        self_username: selfUsername || value.self_username,
        other_username: checked ? value.other_username : undefined,
      });
    },
    [value, setValue, selfUsername],
  );

  const handleOtherUsernameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue({
        ...value,
        self_username: selfUsername || value.self_username,
        other_username: e.target.value?.trim() || undefined,
      });
    },
    [value, setValue, selfUsername],
  );

  const error = Boolean(rawErrors?.length) || Boolean(identityError);
  const raisingForOther = Boolean(value.raising_for_other);
  const displaySelfUsername =
    selfUsername || value.self_username?.trim() || '';

  return (
    <FormControl margin="normal" error={error} fullWidth>
      {!raisingForOther &&
        (loadingIdentity ? (
          <Box display="flex" alignItems="center" style={{ marginTop: 8 }}>
            <CircularProgress size={20} style={{ marginRight: 8 }} />
            <span>Loading your GitLab username…</span>
          </Box>
        ) : (
          <TextField
            margin="normal"
            fullWidth
            label="Your GitLab username"
            value={displaySelfUsername}
            variant="outlined"
            helperText="Filled from your signed-in Iron Phoenix / GitLab identity."
            inputProps={{ readOnly: true }}
          />
        ))}

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={raisingForOther}
              onChange={handleRaisingForOther}
              color="primary"
            />
          }
          label="Raising for other user"
        />
      </Box>

      {raisingForOther && (
        <TextField
          margin="normal"
          fullWidth
          required
          label="Other user's GitLab username"
          placeholder="GitLab username of the person receiving access"
          value={value.other_username ?? ''}
          onChange={handleOtherUsernameChange}
          variant="outlined"
          helperText="Must match an Iron Phoenix catalog User (synced from USER_GROUP)."
        />
      )}

      {/* Only identityError here — scaffolder already renders rawErrors once. */}
      {identityError && !rawErrors?.length ? (
        <FormHelperText error>{identityError}</FormHelperText>
      ) : null}
    </FormControl>
  );
};
